import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bell, FileSearch, Globe, Filter, Building2, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import LanguageSelector from "@/components/LanguageSelector";

export default function MonitoraggioBandiUe() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Monitoraggio Appalti Europei in Tempo Reale"
        description="Ottimizza il tuo monitoraggio degli appalti europei. Ricevi alert personalizzati e analisi IA per non perdere nessuna gara sul TED."
        path="/it/monitoraggio-bandi-ue"
        alternates={[
          { lang: "it", href: "https://tedmonitor.eu/it/monitoraggio-bandi-ue" },
          { lang: "en", href: "https://tedmonitor.eu/eu-tender-monitoring" },
          { lang: "x-default", href: "https://tedmonitor.eu/eu-tender-monitoring" },
        ]}
      />

      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-mark.png" alt="TED Monitor" className="h-8 w-8" />
            <span className="font-semibold text-lg text-primary">TED Monitor</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <Button asChild>
              <Link to="/auth">Inizia ora</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="py-8 md:py-12 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4 leading-tight">
              Monitoraggio Appalti Europei in Tempo Reale
            </h1>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              Le opportunità di appalto pubblico vengono pubblicate ogni giorno in più paesi, stazioni appaltanti,
              lingue e procedure. TED Monitor ti aiuta a monitorare i bandi UE in modo strutturato e centralizzato,
              basandosi sui dati ufficiali europei.
            </p>

            {/* Internal links block (SEO + crawling) */}
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/it/alert-bandi-ted"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Alert bandi TED
              </Link>
              <Link
                to="/it/software-appalti-pubblici-europei"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Software appalti pubblici europei
              </Link>
              <Link
                to="/it/accesso-multilingue"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Accesso multilingue (24 lingue UE)
              </Link>
              <Link
                to="/it/conformita-sicurezza"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Conformità e sicurezza
              </Link>
              <Link
                to="/it/prezzi"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Prezzi
              </Link>
            </div>
          </div>
        </section>

        {/* Cos'è il monitoraggio bandi UE */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <FileSearch className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Cos'è il monitoraggio bandi UE</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Il monitoraggio bandi UE è il tracciamento sistematico degli appalti pubblici pubblicati dalle stazioni
              appaltanti nei paesi dell'Unione Europea. Aiuta le organizzazioni a identificare opportunità rilevanti,
              ridurre il rumore e reagire rapidamente alle nuove pubblicazioni.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">Tipicamente include:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Avvisi di gara e annunci di appalto",
                "Bandi UE sopra soglia",
                "Procedure aperte, ristrette e negoziate",
                "Avvisi di aggiudicazione e risultati",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground leading-relaxed text-sm">
              TED Monitor si basa su fonti ufficiali UE per fornire dati sui bandi affidabili e continuamente aggiornati
              per il monitoraggio e l'analisi.
            </p>
          </div>
        </section>

        {/* Dati ufficiali UE da TED */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Globe className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Dati ufficiali UE da TED</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Tutti i bandi monitorati provengono da{" "}
              <a
                href="https://ted.europa.eu"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Tenders Electronic Daily (TED)
              </a>
              , il supplemento ufficiale della Gazzetta Ufficiale dell'Unione Europea dedicato agli appalti pubblici.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">Con TED Monitor puoi:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm">
              {[
                "Accedere ai bandi pubblicati in tutti gli Stati membri UE",
                "Consultare risultati in formato strutturato e comparabile",
                "Ridurre la frammentazione tra i portali nazionali",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="mt-4">
              <Link to="/data-source" className="text-primary hover:underline font-medium text-sm">
                Scopri di più sulla fonte dati →
              </Link>
            </p>
          </div>
        </section>

        {/* Copertura in tutti i paesi UE */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Building2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Copertura in tutti i paesi UE</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              TED Monitor copre gli Stati membri UE per aiutarti a costruire una visione unificata del mercato europeo
              degli appalti pubblici. Questo è particolarmente utile per l'espansione transfrontaliera, la ricerca di
              partner e lo sviluppo della pipeline di gare.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">Il monitoraggio include:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm">
              {[
                "Stazioni appaltanti centrali e locali",
                "Enti pubblici e organismi di diritto pubblico",
                "Settori classici e settori speciali (utilities, trasporti, ecc.)",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Monitora per codici CPV */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Filter className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Monitora i bandi UE per codici CPV</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              I bandi UE sono classificati per CPV (Vocabolario Comune per gli Appalti). I codici CPV ti permettono di
              identificare le categorie di appalto con precisione e condurre un monitoraggio mirato.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">Con TED Monitor puoi:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Filtrare i bandi per codice CPV",
                "Concentrarti sulle categorie di appalto target",
                "Ridurre i risultati irrilevanti e migliorare il rapporto segnale/rumore",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground leading-relaxed text-sm">
              Questo consente un monitoraggio rilevante anche in mercati di appalto altamente competitivi.
            </p>
          </div>
        </section>

        {/* Alert sui bandi */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Bell className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Alert sui bandi per monitoraggio in tempo reale
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Un monitoraggio efficace richiede velocità. TED Monitor ti permette di configurare criteri di monitoraggio
              e ricevere notifiche quando nuovi bandi corrispondono ai tuoi requisiti.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">Gli alert possono essere configurati in base a:</p>
            <div className="flex flex-wrap gap-2">
              {["CPV", "Paese", "Tipo procedura", "Valore stimato"].map((tag) => (
                <span key={tag} className="px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs font-medium">
                  {tag}
                </span>
              ))}
            </div>

            <p className="mt-4">
              <Link to="/it/alert-bandi-ted" className="text-primary hover:underline font-medium text-sm">
                Esplora gli alert bandi TED →
              </Link>
            </p>
          </div>
        </section>

        {/* Perché usare TED Monitor */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
              Perché usare TED Monitor per il monitoraggio bandi UE
            </h2>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Rispetto alla consultazione manuale di TED o dei portali nazionali frammentati, TED Monitor offre un
              workflow strutturato per costruire una pipeline di gare e tracciare le opportunità sistematicamente.
            </p>

            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Una visione unificata dei bandi UE",
                "Filtri avanzati per risultati rilevanti",
                "Dati strutturati e comparabili",
                "Risparmio di tempo operativo",
                "Copertura più ampia delle opportunità",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground mb-3 text-sm">Progettato per:</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {["Aziende", "Consulenti", "Studi professionali", "Team procurement"].map((item) => (
                <div key={item} className="p-2 bg-background rounded-lg border text-center">
                  <p className="font-medium text-foreground text-xs">{item}</p>
                </div>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap gap-3 text-sm">
              <Link to="/it/software-appalti-pubblici-europei" className="text-primary hover:underline font-medium">
                Software appalti pubblici europei →
              </Link>
              <Link to="/it/accesso-multilingue" className="text-primary hover:underline font-medium">
                Accesso multilingue →
              </Link>
              <Link to="/it/conformita-sicurezza" className="text-primary hover:underline font-medium">
                Conformità e sicurezza →
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-8 md:py-12 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-xl md:text-2xl font-semibold mb-4">Inizia a monitorare i bandi UE</h2>
            <p className="text-base mb-6 opacity-90">
              Costruisci una pipeline di gare affidabile con monitoraggio strutturato, dati TED ufficiali e workflow
              basati su alert.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="default" variant="secondary" asChild>
                <Link to="/it/prezzi" className="gap-2">
                  Vedi i prezzi
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="default"
                variant="outline"
                className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                asChild
              >
                <Link to="/auth">Inizia ora</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo-mark.png" alt="TED Monitor" className="h-6 w-6" />
              <span className="text-sm text-muted-foreground">© {new Date().getFullYear()} TED Monitor</span>
            </div>
            <nav className="flex flex-wrap gap-6 text-sm">
              <Link to="/it/prezzi" className="text-muted-foreground hover:text-foreground">
                Prezzi
              </Link>
              <Link to="/data-source" className="text-muted-foreground hover:text-foreground">
                Fonte dati
              </Link>
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">
                Privacy Policy
              </Link>
              <Link to="/terms-conditions" className="text-muted-foreground hover:text-foreground">
                Termini
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
